import { z } from 'zod';

export const MARKET_STATUSES = ['ok', 'not_found', 'blocked', 'error'];
const MARKET_STATUS_SET = new Set(MARKET_STATUSES);

const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[,']/g, '').trim();
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const providerAttemptSchema = z.object({
  provider: z.string().optional(),
  status: z.string().optional(),
}).passthrough();

// JSON APIs often send explicit `null`; z.string().optional() rejects null — use .nullish().
const marketResponseSchema = z.object({
  status: z.enum(MARKET_STATUSES),
  source: z.string().nullish(),
  source_url: z.string().nullish(),
  message: z.string().nullish(),
  price_per_sqm: z.union([z.number(), z.string()]).nullish(),
  price_min: z.union([z.number(), z.string()]).nullish(),
  price_max: z.union([z.number(), z.string()]).nullish(),
  providers_tried: z.array(providerAttemptSchema).nullish(),
  property_type: z.string().nullish(),
  location: z.string().nullish(),
  data_type: z.string().nullish(),
  apartment_price: z.union([z.number(), z.string()]).nullish(),
  house_price: z.union([z.number(), z.string()]).nullish(),
  apartment_price_min: z.union([z.number(), z.string()]).nullish(),
  apartment_price_max: z.union([z.number(), z.string()]).nullish(),
  house_price_min: z.union([z.number(), z.string()]).nullish(),
  house_price_max: z.union([z.number(), z.string()]).nullish(),
  street_average: z.union([z.number(), z.string()]).nullish(),
  price_date: z.union([z.number(), z.string()]).nullish(),
  quote_currency: z.string().nullish(),
  country_code: z.string().nullish(),
}).passthrough();

export function isKnownMarketStatus(status) {
  return MARKET_STATUS_SET.has(status);
}

/** Stable address key for market AI cache (page reload must not refetch). */
export function buildMarketQueryKey(address) {
  return String(address || '').trim().toLowerCase();
}

export function marketCacheMatchesAddress(cached, address) {
  if (!cached || typeof cached !== 'object') return false;
  if (cached.status !== 'ok') return false;
  const isAi = cached.data_type === 'ai_estimate' || cached.source === 'AI Estimate';
  if (!isAi) return false;
  const key = buildMarketQueryKey(address);
  if (!key || key.length < 5) return false;
  return cached.query_key === key;
}

/**
 * Whether cached market AI is usable for the current country / operating currency.
 * Rejects Swiss CHF quotes when screening Jamaica (etc.).
 */
export function marketCacheMatchesLocale(cached, { countryCode = 'CH', operatingCurrency = 'CHF' } = {}) {
  if (!cached || typeof cached !== 'object' || cached.status !== 'ok') return false;
  const country = String(countryCode || 'CH').toUpperCase();
  const opCur = String(operatingCurrency || 'CHF').toUpperCase();
  const cachedCountry = (cached.country_code || '').toUpperCase();
  const cachedQuote = (cached.quote_currency || '').toUpperCase();
  // Legacy cache (no country / quote): only valid for Switzerland.
  if (!cachedCountry && !cachedQuote) {
    return country === 'CH';
  }
  if (cachedCountry && cachedCountry !== country) return false;
  const expectedQuote = country === 'CH' ? 'CHF' : opCur;
  if (cachedQuote && cachedQuote !== expectedQuote) return false;
  return true;
}

export function normalizeMarketResponse(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      status: 'error',
      message: 'Market response contract mismatch',
      validation_error: true,
      providers_tried: [],
    };
  }

  const parsed = marketResponseSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      status: 'error',
      message: 'Market response contract mismatch',
      validation_error: true,
      providers_tried: [],
    };
  }

  const data = parsed.data;
  const normalized = {
    ...data,
    status: data.status,
    source: data.source || null,
    source_url: data.source_url || null,
    message: data.message || null,
    price_per_sqm: toNumber(data.price_per_sqm),
    price_min: toNumber(data.price_min),
    price_max: toNumber(data.price_max),
    apartment_price: toNumber(data.apartment_price),
    house_price: toNumber(data.house_price),
    apartment_price_min: toNumber(data.apartment_price_min),
    apartment_price_max: toNumber(data.apartment_price_max),
    house_price_min: toNumber(data.house_price_min),
    house_price_max: toNumber(data.house_price_max),
    street_average: toNumber(data.street_average),
    price_date: toNumber(data.price_date),
    quote_currency: (data.quote_currency || null)
      ? String(data.quote_currency).trim().toUpperCase()
      : null,
    country_code: (data.country_code || null)
      ? String(data.country_code).trim().toUpperCase()
      : null,
    providers_tried: Array.isArray(data.providers_tried) ? data.providers_tried : [],
    validation_error: false,
  };

  if (normalized.status === 'ok' && (!normalized.price_per_sqm || normalized.price_per_sqm <= 0)) {
    return {
      ...normalized,
      status: 'error',
      message: 'Market response missing price_per_sqm',
      validation_error: true,
    };
  }

  return normalized;
}

export function resolveMarketViewState({ loading, result }) {
  if (loading) return 'loading';
  if (!result) return 'idle';
  if (result.status === 'ok') return 'ok';
  if (result.status === 'not_found') return 'not_found';
  if (result.status === 'blocked') return 'blocked';
  if (result.status === 'error') return 'error';
  return 'unknown';
}

function extractPostcode(text) {
  if (!text || typeof text !== 'string') return '';
  // Match Swiss 4-digit postcodes even in punctuation-heavy address strings.
  const m = text.match(/(?:^|[^0-9])([1-9][0-9]{3})(?:[^0-9]|$)/);
  return m ? m[1] : '';
}

export function resolveLocationContext({ locationData, cadastreData, address }) {
  const locationPostcode = locationData?.postcode || '';
  const locationCity = locationData?.city || '';
  const cadastrePostcode = extractPostcode(cadastreData?.address || '');
  const addressPostcode = extractPostcode(address || '');
  return {
    postcode: locationPostcode || cadastrePostcode || addressPostcode,
    city: locationCity || cadastreData?.commune || '',
  };
}

// Backward-compatible exports for existing tests/imports.
export const KNOWN_MARKET_STATUSES = MARKET_STATUS_SET;
export const normalizeMarketPayload = normalizeMarketResponse;
export const deriveMarketViewState = resolveMarketViewState;
export const resolveMarketLocation = resolveLocationContext;
